/**
 * Hit testing and selection geometry for markups and takeoffs (page space,
 * PDF points). Rotation is about the centre of `bounds`, as Centerline draws it;
 * `vertices` live in the unrotated frame.
 */
import type { Markup, Measurement, Vec2 } from "./types.js";

export function rotatePoint([x, y]: Vec2, [cx, cy]: Vec2, degrees: number): Vec2 {
  if (!degrees) return [x, y];
  const r = (degrees * Math.PI) / 180, c = Math.cos(r), s = Math.sin(r);
  const dx = x - cx, dy = y - cy;
  return [cx + dx * c - dy * s, cy + dx * s + dy * c];
}

export function boundsCenter(b: readonly number[]): Vec2 {
  return [b[0]! + b[2]! / 2, b[1]! + b[3]! / 2];
}

export function distanceToSegment([px, py]: Vec2, [ax, ay]: Vec2, [bx, by]: Vec2): number {
  const dx = bx - ax, dy = by - ay;
  const len2 = dx * dx + dy * dy;
  const t = len2 ? Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2)) : 0;
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

export function distanceToPath(p: Vec2, pts: readonly Vec2[], closed: boolean): number {
  if (pts.length === 0) return Infinity;
  if (pts.length === 1) return Math.hypot(p[0] - pts[0]![0], p[1] - pts[0]![1]);
  let d = Infinity;
  for (let i = 1; i < pts.length; i++) d = Math.min(d, distanceToSegment(p, pts[i - 1]!, pts[i]!));
  if (closed && pts.length > 2) d = Math.min(d, distanceToSegment(p, pts[pts.length - 1]!, pts[0]!));
  return d;
}

export function insidePolygon([x, y]: Vec2, pts: readonly Vec2[]): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, yi] = pts[i]!, [xj, yj] = pts[j]!;
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** True when page point `p` is on the markup (within `tol` points). */
export function markupHit(m: Markup, p: Vec2, tol: number): boolean {
  const local = rotatePoint(p, boundsCenter(m.bounds), -m.rotationDegrees);
  const [x, y, w, h] = m.bounds;
  if (m.kind === "quickMeasure") {
    const v = m.vertices ?? [];
    const closed = m.style.closed;
    return distanceToPath(local, v, closed) <= tol + 2 || (closed && v.length > 2 && insidePolygon(local, v));
  }
  if ((m.kind === "workArea" || m.kind === "cloud") && m.vertices && m.vertices.length > 2) {
    return insidePolygon(local, m.vertices) || distanceToPath(local, m.vertices, true) <= tol + (m.kind === "cloud" ? 10 : 2);
  }
  return local[0] >= x - tol && local[0] <= x + w + tol && local[1] >= y - tol && local[1] <= y + h + tol;
}

export function measurementHit(m: Measurement, p: Vec2, tol: number): boolean {
  if (!m.points.length) return false;
  switch (m.kind) {
    case "count": return m.points.some(([x, y]) => Math.hypot(p[0] - x, p[1] - y) <= 8 + tol);
    case "area": return (m.points.length > 2 && insidePolygon(p, m.points) && !(m.cutouts ?? []).some((c) => insidePolygon(p, c))) || distanceToPath(p, m.points, true) <= tol + 2;
    default: return distanceToPath(p, m.points, false) <= tol + 2;
  }
}

/** The four corners of the (rotated) bounds, for a selection outline. */
export function markupCorners(m: Markup): Vec2[] {
  const [x, y, w, h] = m.bounds;
  const c = boundsCenter(m.bounds);
  return ([[x, y], [x + w, y], [x + w, y + h], [x, y + h]] as Vec2[]).map((q) => rotatePoint(q, c, m.rotationDegrees));
}

export function pointsBox(pts: readonly Vec2[]): [number, number, number, number] {
  if (!pts.length) return [0, 0, 0, 0];
  const xs = pts.map((q) => q[0]), ys = pts.map((q) => q[1]);
  const x = Math.min(...xs), y = Math.min(...ys);
  return [x, y, Math.max(...xs) - x, Math.max(...ys) - y];
}

/** Topmost hit on a page: markups are on top of takeoffs; later items draw over earlier ones. */
export function hitTest(
  doc: { markups: readonly Markup[]; measurements: readonly Measurement[] },
  pageId: string, p: Vec2, tol: number, visibleLayers: readonly string[] | null,
): { kind: "markup" | "measurement"; id: string } | null {
  const visible = (layer: string) => !visibleLayers || visibleLayers.includes(layer);
  for (let i = doc.markups.length - 1; i >= 0; i--) {
    const m = doc.markups[i]!;
    if (m.pageId === pageId && visible(m.layer) && markupHit(m, p, tol)) return { kind: "markup", id: m.id };
  }
  for (let i = doc.measurements.length - 1; i >= 0; i--) {
    const m = doc.measurements[i]!;
    if (m.pageId === pageId && visible(m.layer) && measurementHit(m, p, tol)) return { kind: "measurement", id: m.id };
  }
  return null;
}
